import { useCallback, useLayoutEffect } from 'react';
import type { ChangeEvent, KeyboardEvent, RefObject } from 'react';
import { motion } from 'motion/react';
import { Send, Square, X } from 'lucide-react';
import MemoryPanel from '../../components/AIChat/MemoryPanel';
import ModePicker from '../../components/AIChat/ModePicker';
import SlashCommandPicker from '../../components/AIChat/SlashCommandPicker';
import { resizeTextarea } from '../../components/ui/AutoGrowTextarea';
import { inputArea, inputTextarea, sendActiveClass, sendInactiveClass, stopButtonClass } from '../../components/AIChat/chatViewTheme';
import type { SlashCommand } from '../../components/AIChat/slashCommands';

const MAX_INPUT_HEIGHT_PX = 224; // 14rem

interface VantiWindowInputProps {
  memoryPanelOpen: boolean;
  onCloseMemory: () => void;
  input: string;
  inputRef: RefObject<HTMLTextAreaElement>;
  onInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onStop: () => void;
  loading: boolean;
  streamingMessageId: string | null;
  onCooldown: boolean | null;
  chatMode: string;
  onModeChange: (mode: string) => void;
  scopeChip?: string | null;
  onClearScope?: () => void;
  reduceMotion?: boolean | null;
  // slash commands
  slashPickerOpen?: boolean;
  slashQuery?: string;
  slashSelectedIndex?: number;
  onSlashSelect?: (cmd: SlashCommand) => void;
  formatModifier?: { suffix: string; label: string } | null;
  onClearFormatModifier?: () => void;
}

export default function VantiWindowInput({
  memoryPanelOpen,
  onCloseMemory,
  input,
  inputRef,
  onInputChange,
  onKeyDown,
  onSend,
  onStop,
  loading,
  streamingMessageId,
  onCooldown,
  chatMode,
  onModeChange,
  scopeChip,
  onClearScope,
  reduceMotion,
  slashPickerOpen = false,
  slashQuery = '',
  slashSelectedIndex = 0,
  onSlashSelect,
  formatModifier,
  onClearFormatModifier,
}: VantiWindowInputProps) {
  const canSend = !onCooldown && input.trim();

  useLayoutEffect(() => {
    resizeTextarea(inputRef.current, MAX_INPUT_HEIGHT_PX);
  });

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onInputChange(e);
      resizeTextarea(e.target, MAX_INPUT_HEIGHT_PX);
    },
    [onInputChange],
  );

  return (
    <div className={`relative ${inputArea}`}>
      <MemoryPanel open={memoryPanelOpen} onClose={onCloseMemory} />

      {/* Slash command picker */}
      <SlashCommandPicker
        open={slashPickerOpen}
        query={slashQuery}
        selectedIndex={slashSelectedIndex}
        onSelect={onSlashSelect ?? (() => {})}
      />

      <div className="flex flex-col gap-1.5">
        {input.length > 18000 && (
          <p className="text-xs text-amber-400">{20000 - input.length} characters remaining</p>
        )}

        {/* Input box with integrated toolbar */}
        <div className="flex flex-col rounded-xl border border-dark-border bg-dark-elevated shadow-sm transition-colors focus-within:border-dark-border-glow">
          <div className="flex items-end gap-2 px-3 py-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={onKeyDown}
              placeholder={
                formatModifier
                  ? `Ask a question — will be presented as ${formatModifier.label.toLowerCase()}…`
                  : 'Ask Vanti a question… (type / for commands)'
              }
              rows={1}
              maxLength={20000}
              className={`${inputTextarea} placeholder-slate-600`}
              disabled={!!onCooldown}
            />

            {loading || streamingMessageId ? (
              <motion.button
                type="button"
                onClick={onStop}
                whileHover={reduceMotion ? undefined : { scale: 1.08 }}
                whileTap={reduceMotion ? undefined : { scale: 0.92 }}
                className={stopButtonClass}
                aria-label="Stop generating"
                title="Stop generating"
              >
                <Square className="h-4 w-4 fill-current" aria-hidden />
              </motion.button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                className={canSend ? sendActiveClass : sendInactiveClass}
                aria-label="Send"
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>

          {/* Context badges */}
          {(formatModifier || scopeChip) && (
            <div className="flex items-center gap-2 px-3 pb-2.5">
            {/* Format modifier badge */}
            {formatModifier && (
              <span className="flex items-center gap-1 rounded-full border border-amber-700/40 bg-amber-900/20 px-2.5 py-1 text-[11px] text-amber-300">
                {formatModifier.label}
                <button
                  type="button"
                  onClick={onClearFormatModifier}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-amber-800/30"
                  aria-label="Clear format modifier"
                >
                  <X className="h-2.5 w-2.5" aria-hidden />
                </button>
              </span>
            )}

            {/* Scope pill */}
            {scopeChip && !formatModifier && (
              <span className="flex items-center gap-1 rounded-full border border-brand-purple/30 bg-brand-purple/10 px-2.5 py-1 text-[11px] font-medium text-brand-purple-light">
                {scopeChip}
                {onClearScope && (
                  <button
                    type="button"
                    onClick={onClearScope}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-brand-purple/20"
                    aria-label="Clear scope filter"
                    title="Clear scope"
                  >
                    <X className="h-2.5 w-2.5" aria-hidden />
                  </button>
                )}
              </span>
            )}
            </div>
          )}
        </div>

        <div className="flex items-center px-1">
          <ModePicker value={chatMode} onChange={onModeChange} />
        </div>

        <p className="select-none text-center text-[11px] text-slate-700">
          <kbd className="font-sans">↵</kbd> to send&nbsp;·&nbsp;
          <kbd className="font-sans">⇧↵</kbd> new line&nbsp;·&nbsp;
          <kbd className="font-sans">/</kbd> commands
        </p>
      </div>
    </div>
  );
}
